import { io, Socket } from "socket.io-client"
import {
  MatchUpdatedPayload,
  TournamentBroadcastEvent,
  TournamentJoinedPayload,
  TournamentUpdatedPayload,
  VirtualTournamentDetail,
  VirtualTournamentMatch,
} from "../types/virtualTournament"
import {
  normalizeVirtualTournamentDetail,
  normalizeVirtualTournamentMatch,
} from "../utils/virtualTournamentNormalize"

import { getSocketBaseUrl } from "@/lib/socketBase"

const API_BASE = getSocketBaseUrl()

type TournamentEmitEvents = {
  "join-tournament": (payload: { tournamentId: string }) => void
}

type TournamentListenEvents = {
  "tournament-joined": (payload: TournamentJoinedPayload) => void
  "tournament-updated": (payload: TournamentUpdatedPayload) => void
  "match-updated": (payload: MatchUpdatedPayload) => void
  "broadcast-event": (payload: TournamentBroadcastEvent) => void
}

type RegisteredListener = {
  event: keyof TournamentListenEvents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (...args: any[]) => void
}

let socket: Socket | null = null
let lastAuthToken: string | null = null
let connectPromise: Promise<Socket> | null = null
let connectPromiseToken: string | null = null

const registeredListeners: RegisteredListener[] = []

function normalizeAuthToken(raw: string | null | undefined): string {
  return raw?.replace(/^Bearer\s+/i, "").trim() ?? ""
}

function bindListener(sock: Socket, entry: RegisteredListener) {
  const s = sock as Socket & {
    on: (ev: string, fn: (...args: unknown[]) => void) => void
    off: (ev: string, fn: (...args: unknown[]) => void) => void
  }
  s.off(entry.event, entry.handler as (...args: unknown[]) => void)
  s.on(entry.event, entry.handler as (...args: unknown[]) => void)
}

function bindAllRegisteredListeners(sock: Socket) {
  for (const entry of registeredListeners) {
    bindListener(sock, entry)
  }
}

function createTournamentSocket(token: string): Socket {
  const sock = io(`${API_BASE}/game/tournament`, {
    auth: { token },
    transports: ["websocket", "polling"],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })
  bindAllRegisteredListeners(sock)
  return sock
}

function clearConnectPromise(token: string) {
  if (connectPromiseToken === token) {
    connectPromise = null
    connectPromiseToken = null
  }
}

function teardownSocket() {
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
}

function waitForSocketConnect(sock: Socket): Promise<Socket> {
  if (sock.connected) return Promise.resolve(sock)

  return new Promise((resolve, reject) => {
    const onConnect = () => {
      cleanup()
      resolve(sock)
    }
    const onError = (err: Error) => {
      cleanup()
      reject(err)
    }
    const cleanup = () => {
      sock.off("connect", onConnect)
      sock.off("connect_error", onError)
    }

    sock.once("connect", onConnect)
    sock.once("connect_error", onError)
    if (!sock.connected) {
      sock.connect()
    }
  })
}

export function connectTournamentSocket(token: string): Promise<Socket> {
  const normalized = normalizeAuthToken(token)
  if (!normalized) {
    return Promise.reject(new Error("Tournament socket: missing auth token"))
  }

  if (socket?.connected && lastAuthToken === normalized) {
    return Promise.resolve(socket)
  }

  if (connectPromise && connectPromiseToken === normalized) {
    return connectPromise
  }

  if (socket && lastAuthToken !== normalized) {
    teardownSocket()
  }

  connectPromiseToken = normalized
  connectPromise = (async () => {
    if (!socket || lastAuthToken !== normalized) {
      lastAuthToken = normalized
      socket = createTournamentSocket(normalized)
    }
    return waitForSocketConnect(socket)
  })().finally(() => clearConnectPromise(normalized))

  return connectPromise
}

export function disconnectTournamentSocket() {
  teardownSocket()
  lastAuthToken = null
  connectPromise = null
  connectPromiseToken = null
}

export function emitTournamentEvent<E extends keyof TournamentEmitEvents>(
  event: E,
  ...args: Parameters<TournamentEmitEvents[E]>
) {
  if (!socket?.connected) return
  socket.emit(event, ...args)
}

export function onTournamentEvent<E extends keyof TournamentListenEvents>(
  event: E,
  handler: TournamentListenEvents[E]
): () => void {
  const entry: RegisteredListener = { event, handler }
  registeredListeners.push(entry)
  if (socket) {
    bindListener(socket, entry)
  }
  return () => {
    const idx = registeredListeners.indexOf(entry)
    if (idx >= 0) registeredListeners.splice(idx, 1)
    ;(socket as Socket & { off: (ev: string, fn: (...args: unknown[]) => void) => void } | null)?.off(
      event,
      handler as (...args: unknown[]) => void
    )
  }
}

export function joinTournament(tournamentId: string) {
  emitTournamentEvent("join-tournament", { tournamentId })
}

export function parseTournamentJoinedPayload(
  raw: unknown
): TournamentJoinedPayload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const tournamentId = String(obj.tournamentId ?? obj.tournament_id ?? "").trim()
  if (!tournamentId) return null
  const snapshot = normalizeVirtualTournamentDetail(obj.snapshot ?? obj.tournament)
  if (!snapshot) return null
  const liveMatchRaw = obj.liveMatch ?? obj.live_match
  const liveMatch =
    liveMatchRaw != null ? normalizeVirtualTournamentMatch(liveMatchRaw) : null
  return { tournamentId, snapshot, liveMatch }
}

export function mergeTournamentUpdate(
  current: VirtualTournamentDetail,
  update: TournamentUpdatedPayload
): VirtualTournamentDetail {
  return {
    ...current,
    status: update.status ?? current.status,
    participants: update.participants ?? current.participants,
  }
}

export function mergeMatchUpdate(
  current: VirtualTournamentDetail,
  update: MatchUpdatedPayload
): VirtualTournamentDetail {
  const matches = [...current.matches]
  const idx = matches.findIndex((m) => m.id === update.match.id)
  if (idx >= 0) {
    matches[idx] = update.match
  } else {
    matches.push(update.match)
  }
  return {
    ...current,
    matches,
    liveMatchId:
      update.match.status === "IN_PROGRESS" ? update.match.id : current.liveMatchId,
  }
}

export function getLiveMatch(
  detail: VirtualTournamentDetail
): VirtualTournamentMatch | null {
  if (!detail.liveMatchId) return null
  return detail.matches.find((m) => m.id === detail.liveMatchId) ?? null
}
